// @module Ames.RentFromAnywhere.RentalGrid
// Custom Content Type view: grid of rental tools with quantity, rental
// period, price/day, period total and a "Rent Now" add-to-cart button.
define("Ames.RentFromAnywhere.RentalGrid.View", [
  "CustomContentType.Base.View",
  "ames_rentfromanywhere_rentalgrid.tpl",
  "ames_rentfromanywhere_rentaldetails.tpl",
  "Backbone",
  "jQuery",
  "underscore",
  "Utils",
  "Ames.RentFromAnywhere.AccessGate"
], function (
  CustomContentTypeBaseView,
  ames_rentfromanywhere_rentalgrid_tpl,
  ames_rentfromanywhere_rentaldetails_tpl,
  Backbone,
  jQuery,
  _,
  Utils,
  AccessGate
) {
  "use strict";

  // Placeholder the grid relocates into when present on the page
  // (put <div id="rfa-rental-grid-slot"></div> in the landing page HTML block)
  var SLOT_SELECTOR = "#rfa-rental-grid-slot, [data-rfa-rental-grid-slot]";

  // Keep in sync with Ames.RentFromAnywhere.RentFromAnywhere.View (PDP)
  var PERIODS = [
    { key: "8-day",   days: 8,  label: "8 Days",  cartLabel: "8 Days Rental",  rateField: "custitem_nst_rrm_rt_daily" },
    { key: "16-days", days: 16, label: "16 Days", cartLabel: "16 Days Rental", rateField: "custitem_nst_rrm_rt_weekly" },
    { key: "30-days", days: 30, label: "30 Days", cartLabel: "30 Days Rental", rateField: "custitem_nst_rrm_rt_monthly" },
    { key: "60-days", days: 60, label: "60 Days", cartLabel: "60 Days Rental", rateField: "custitem_nst_rrm_60_days" },
    { key: "90-days", days: 90, label: "90 Days", cartLabel: "90 Days Rental", rateField: "custitem_nst_rrm_90_days" }
  ];

  var DEFAULTS = {
    defaultPeriod: "90-days",
    pageSize: 9,
    fieldset: "search",
    maxQuantity: 99,
    // manual: custitem_rfa_rental_sort (lower first), then price high-to-low
    // price-desc / price-asc: total for the default period | name
    sort: "manual"
  };
  var SORT_MODES = ["manual", "price-desc", "price-asc", "name"];

  // Quick-view style popup: image + Detailed Description
  var DetailsView = Backbone.View.extend({
    template: ames_rentfromanywhere_rentaldetails_tpl,
    className: "rental-details",
    initialize: function (options) { this.card = options.card; },
    getContext: function () {
      var c = this.card;
      return {
        name: c.name,
        image: c.image,
        hasImage: !!c.image,
        url: c.url,
        hasUrl: !!c.url,
        isKit: c.isKit,
        description: c.description || "",
        hasDescription: !!(c.description && String(c.description).replace(/<[^>]*>/g, "").trim()),
        components: _.map(c.components || [], function (k) {
          return { sku: k.sku, name: k.name, quantity: k.quantity, showQty: k.quantity > 1 };
        }),
        hasComponents: !!(c.components && c.components.length)
      };
    }
  });

  var SERVICE_URL = Utils.getAbsoluteUrl(
    getExtensionAssetsPath("Modules/RentFromAnywhere/SuiteScript2/RentalItems.Service.ss"),
    true
  );

  var SEARCH_API_CHUNK = 50;

  // NetSuite flags arrive as booleans or "T"/"F" strings depending on the fieldset
  function isTruthyFlag(v, fallback) {
    if (v === undefined || v === null || v === "") return fallback;
    return v === true || v === "T" || v === "true";
  }

  // Walk itemimages_detail (arbitrarily nested folders) and return the first url
  function firstImageUrl(detail) {
    if (!detail) return null;
    if (_.isArray(detail)) {
      for (var i = 0; i < detail.length; i++) {
        var u = firstImageUrl(detail[i]);
        if (u) return u;
      }
      return null;
    }
    if (_.isObject(detail)) {
      if (detail.url) return detail.url;
      var keys = _.keys(detail);
      for (var k = 0; k < keys.length; k++) {
        var v = firstImageUrl(detail[keys[k]]);
        if (v) return v;
      }
    }
    return null;
  }

  // Utils.resizeImage is (sizes, url, size) in this SCA version (older
  // builds used (url, size)). Try the site's image sizes first and only
  // accept a result that still contains the original URL.
  function resizeImage(url) {
    if (!url) return null;
    try {
      if (_.isFunction(Utils.resizeImage)) {
        var env = window.SC && SC.ENVIRONMENT;
        var sizes = env && env.siteSettings && env.siteSettings.imagesizes;
        var out = sizes ? Utils.resizeImage(sizes, url, "thumbnail") : Utils.resizeImage(url, "thumbnail");
        if (_.isString(out) && out.indexOf(url) === 0) return out;
      }
    } catch (e) { /* fall through */ }
    return url;
  }

  // Theme's "no image available" placeholder (same one the PDP shows),
  // e.g. /scs/img/no_image_available.jpg?resizeid=...
  function noImageUrl() {
    try {
      var url = _.isFunction(Utils.getThemeAbsoluteUrlOfNonManagedResources)
        ? Utils.getThemeAbsoluteUrlOfNonManagedResources("img/no_image_available.jpeg")
        : Utils.getAbsoluteUrl("img/no_image_available.jpeg");
      return resizeImage(url);
    } catch (e) {
      return null;
    }
  }

  // @class Ames.RentFromAnywhere.RentalGrid.View @extends CustomContentType.Base.View
  return CustomContentTypeBaseView.extend({
    template: ames_rentfromanywhere_rentalgrid_tpl,

    // Set via .extend({ application: container }) at registration time
    application: null,

    events: {
      'click [data-action="qty-minus"]': "onQtyMinus",
      'click [data-action="qty-plus"]': "onQtyPlus",
      'change [data-action="qty-input"]': "onQtyInput",
      'change [data-action="select-period"]': "onPeriodChange",
      'click [data-action="pick-period"]': "onPeriodPick",
      'keydown [data-action="pick-period"]': "onPeriodKey",
      'click [data-action="open-details"]': "onOpenDetails",
      'click [data-action="toggle-sort"]': "onToggleSort",
      'click [data-action="rent-now"]': "onRentNow",
      'click [data-action="load-more"]': "onLoadMore"
    },

    initialize: function () {
      if (_.isFunction(CustomContentTypeBaseView.prototype.initialize)) {
        CustomContentTypeBaseView.prototype.initialize.apply(this, arguments);
      }
      this.cards = [];
      this.visible = 0;
      this.state = "loading"; // loading | ready | empty | error
      this.errorMessage = "";
    },

    install: function (settings, contextData) {
      if (_.isFunction(CustomContentTypeBaseView.prototype.install)) {
        CustomContentTypeBaseView.prototype.install.apply(this, arguments);
      }
      this.settings = _.extend({}, settings || {});
      this.options = this._readSettings(this.settings);
      this.visible = this.options.pageSize;
      if (AccessGate.isUnlocked()) {
        this._load();
      } else {
        // Client-side gate: show the login form instead of the tools
        this.state = "locked";
        this.render();
      }
    },

    // Called by the gate form (bound in render) once credentials are valid
    _onUnlocked: function () {
      this._load();
    },

    // ---- optional "slot" placement --------------------------------------
    // If any other content on the page (e.g. the landing page HTML block)
    // contains an element matching SLOT_SELECTOR, the grid moves itself into
    // it after rendering, so the tools appear in the middle of that HTML
    // instead of where the CCT sits in the SMT layout. Skipped inside the
    // SMT editor iframe so the CMS outlines / drag-drop keep working; the
    // grid then simply renders in its own CCT position.
    render: function () {
      var ret;
      if (_.isFunction(CustomContentTypeBaseView.prototype.render)) {
        ret = CustomContentTypeBaseView.prototype.render.apply(this, arguments);
      }
      this._scheduleMoveToSlot();
      if (this.state === "locked") {
        var self = this;
        AccessGate.bindForm(this.$el, function () { self._onUnlocked(); });
      }
      return ret;
    },

    _isCmsEditMode: function () {
      try {
        if (window.self !== window.top) return true; // SMT edits the site inside an iframe
        if (window.SC && window.SC.ENVIRONMENT && window.SC.ENVIRONMENT.CMS_EDIT) return true;
      } catch (e) {
        return true;
      }
      return false;
    },

    _scheduleMoveToSlot: function () {
      var self = this;
      if (this._isCmsEditMode()) return;
      // The HTML block may render after us, and SMT re-attaches our element
      // after the first render, so try a few times.
      _.each([0, 300, 1000, 2500], function (delay) {
        setTimeout(function () { self._moveToSlot(); }, delay);
      });
    },

    _moveToSlot: function () {
      var $slot = jQuery(SLOT_SELECTOR).first();
      if (!$slot.length) return;
      if ($slot[0] === this.el.parentNode) return; // already there
      $slot.addClass("rfa-rental-grid-slot-filled").empty().append(this.$el);
    },

    // Settings record fields are optional; sensible defaults otherwise
    _readSettings: function (s) {
      var val = function (key, fallback) {
        var v = s[key];
        if (v && _.isObject(v) && !_.isUndefined(v.name)) v = v.name;
        return (v === undefined || v === null || v === "") ? fallback : v;
      };
      var period = String(val("custrecord_ames_rg_default_period", DEFAULTS.defaultPeriod)).toLowerCase();
      if (!_.findWhere(PERIODS, { key: period })) period = DEFAULTS.defaultPeriod;
      var sort = String(val("custrecord_ames_rg_sort", DEFAULTS.sort)).toLowerCase().replace(/_/g, "-");
      if (!_.contains(SORT_MODES, sort)) sort = DEFAULTS.sort;
      return {
        defaultPeriod: period,
        pageSize: parseInt(val("custrecord_ames_rg_page_size", DEFAULTS.pageSize), 10) || DEFAULTS.pageSize,
        fieldset: String(val("custrecord_ames_rg_fieldset", DEFAULTS.fieldset)),
        heading: val("custrecord_ames_rg_heading", ""),
        sort: sort
      };
    },

    // ---- data loading -------------------------------------------------
    _load: function () {
      var self = this;
      this.state = "loading";

      jQuery.ajax({ url: SERVICE_URL, dataType: "json", cache: false })
        .then(function (svc) {
          if (!svc || !svc.success) {
            throw new Error((svc && svc.message) || "Rental items service failed");
          }
          if (!svc.items || !svc.items.length) {
            return { svc: svc, api: [] };
          }
          return self._fetchFromSearchApi(_.pluck(svc.items, "internalid")).then(function (apiItems) {
            return { svc: svc, api: apiItems };
          });
        })
        .then(function (data) {
          self.cards = self._sortCards(self._buildCards(data.svc.items, data.api));
          self.state = self.cards.length ? "ready" : "empty";
          self.render();
        })
        .fail(function (err) {
          self.state = "error";
          self.errorMessage = (err && err.message) || "Unable to load rental tools right now.";
          self.render();
        });
    },

    // Item Search API URL: root-relative (not under the SSP app folder) and
    // carrying the site/company params (c, n) SCA adds to every search call.
    _searchApiUrl: function (params) {
      if (_.isFunction(Utils.addParamsToUrl)) {
        return Utils.addParamsToUrl("/api/items", params);
      }
      var env = (window.SC && SC.ENVIRONMENT) || {};
      var extra = _.extend({}, params);
      if (env.companyId) extra.c = env.companyId;
      if (env.siteSettings && env.siteSettings.siteid) extra.n = env.siteSettings.siteid;
      return "/api/items?" + jQuery.param(extra);
    },

    // One item without a price makes the Item Search API reject the whole
    // batch ("Please enter a value for Amount"), so fetch in chunks and, when a
    // chunk fails, retry its ids one by one and drop only the failing ones.
    // Cards for dropped items still render from the service data (no image).
    _fetchFromSearchApi: function (ids) {
      var self = this;
      var chunks = [];
      for (var i = 0; i < ids.length; i += SEARCH_API_CHUNK) {
        chunks.push(ids.slice(i, i + SEARCH_API_CHUNK));
      }

      var fetchIds = function (idList) {
        var url = self._searchApiUrl({
          id: idList.join(","),
          fieldset: self.options.fieldset,
          limit: SEARCH_API_CHUNK
        });
        return jQuery.ajax({ url: url, dataType: "json" }).then(function (r) {
          return (r && r.items) || [];
        });
      };

      var fetchChunk = function (chunk) {
        return fetchIds(chunk).then(null, function (err) {
          console.warn("RentalGrid: item batch failed, retrying items individually", err && err.responseJSON);
          var singles = _.map(chunk, function (id) {
            return fetchIds([id]).then(null, function (e) {
              console.warn("RentalGrid: skipping item " + id, e && e.responseJSON && e.responseJSON.errorMessage);
              return jQuery.Deferred().resolve([]).promise();
            });
          });
          return jQuery.when.apply(jQuery, singles).then(function () {
            return _.flatten(_.toArray(arguments), true);
          });
        });
      };

      var requests = _.map(chunks, fetchChunk);
      return jQuery.when.apply(jQuery, requests).then(function () {
        var results = requests.length === 1 ? [arguments[0]] : _.toArray(arguments);
        return _.flatten(results, true);
      });
    },

    _buildCards: function (svcItems, apiItems) {
      var self = this;
      var byId = {};
      _.each(apiItems, function (it) { byId[it.internalid] = it; });
      return _.compact(_.map(svcItems, function (src) {
        var api = byId[src.internalid] || {};
        var rates = {};
        _.each(PERIODS, function (p) {
          var v = !_.isUndefined(api[p.rateField]) && api[p.rateField] !== "" && api[p.rateField] !== null
            ? parseFloat(api[p.rateField])
            : src.rates[p.rateField];
          rates[p.key] = (v === null || isNaN(v)) ? null : v;
        });

        var available = _.filter(PERIODS, function (p) { return rates[p.key] !== null && rates[p.key] > 0; });
        if (!available.length) return null;

        var period = _.findWhere(available, { key: self.options.defaultPeriod }) ? self.options.defaultPeriod : available[0].key;
        var urlcomponent = api.urlcomponent || src.urlcomponent;

        // Rental set (Kit/Package): the Suitelet sends its components; the set
        // is rentable when every component is (a kit's own stock flag is not
        // meaningful, it has no inventory of its own).
        var isKit = !!src.isKit;
        var components = isKit ? _.map(src.components || [], function (c) {
          var capi = byId[c.internalid] || {};
          return {
            internalid: c.internalid,
            sku: c.itemid,
            name: capi.storedisplayname || capi.displayname || c.name,
            quantity: c.quantity,
            inStock: isTruthyFlag(capi.isinstock, true) || isTruthyFlag(capi.isbackorderable, false)
          };
        }) : [];
        var inStock = isKit && components.length
          ? _.every(components, function (c) { return c.inStock; })
          : (isTruthyFlag(api.isinstock, true) || isTruthyFlag(api.isbackorderable, false));

        return {
          internalid: src.internalid,
          name: api.storedisplayname || api.displayname || src.storedisplayname,
          url: urlcomponent ? "/" + urlcomponent : null,
          image: resizeImage(firstImageUrl(api.itemimages_detail)) || noImageUrl(),
          rates: rates,
          availablePeriods: available,
          period: period,
          quantity: 1,
          adding: false,
          isKit: isKit,
          components: components,
          description: src.detaileddescription || api.storedetaileddescription || "",
          sortOrder: (src.sortOrder === null || src.sortOrder === undefined || isNaN(src.sortOrder)) ? null : Number(src.sortOrder),
          // Purchasable when in stock OR back-orderable (out-of-stock behavior
          // "Allow back orders"). Unknown (item not returned by the search API)
          // counts as purchasable.
          inStock: inStock
        };
      }));
    },

    // ---- ordering ----------------------------------------------------------
    // Total for the default period (sets have the highest totals, so
    // "price high-to-low" puts them first)
    _sortValue: function (card) {
      var key = _.findWhere(card.availablePeriods, { key: this.options.defaultPeriod })
        ? this.options.defaultPeriod
        : card.availablePeriods[card.availablePeriods.length - 1].key;
      var p = _.findWhere(PERIODS, { key: key });
      return (card.rates[key] || 0) * p.days;
    },

    _sortCards: function (cards) {
      var self = this;
      // the shopper's choice (sort button) wins over the CCT setting
      var mode = this.userSort || this.options.sort;
      var byName = function (a, b) { return String(a.name).localeCompare(String(b.name)); };
      var byPriceDesc = function (a, b) { return self._sortValue(b) - self._sortValue(a) || byName(a, b); };
      if (mode === "name") return cards.sort(byName);
      if (mode === "price-asc") return cards.sort(function (a, b) { return self._sortValue(a) - self._sortValue(b) || byName(a, b); });
      if (mode === "price-desc") return cards.sort(byPriceDesc);
      // manual: items with a sort order first (ascending), the rest by price high-to-low
      return cards.sort(function (a, b) {
        var ao = a.sortOrder === null, bo = b.sortOrder === null;
        if (!ao && !bo) return a.sortOrder - b.sortOrder || byPriceDesc(a, b);
        if (!ao) return -1;
        if (!bo) return 1;
        return byPriceDesc(a, b);
      });
    },

    // Sort button: toggles price high-to-low / low-to-high. Cards keep their
    // state (period, quantity) because the same objects are re-ordered.
    onToggleSort: function (e) {
      e.preventDefault();
      var current = this.userSort || this.options.sort;
      this.userSort = current === "price-asc" ? "price-desc" : "price-asc";
      this.cards = this._sortCards(this.cards);
      this.render();
    },

    // ---- details popup (image + Detailed Description) ----------------------
    onOpenDetails: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var card = this._cardFromEvent(e);
      if (!card) return;
      var view = new DetailsView({ card: card });
      var layout = this.application && _.isFunction(this.application.getLayout) ? this.application.getLayout() : null;
      if (layout && _.isFunction(layout.showInModal)) {
        layout.showInModal(view, { className: "rental-details-modal" });
      } else {
        this._showFallbackModal(view);
      }
    },

    // Minimal modal when the SCA layout is not reachable (e.g. CCT preview)
    _showFallbackModal: function (view) {
      var $wrap = jQuery('<div class="rental-details-fallback"><div class="rental-details-fallback-backdrop"></div>' +
        '<div class="rental-details-fallback-box"><button type="button" class="rental-details-fallback-close" aria-label="Close">&times;</button></div></div>');
      view.render();
      $wrap.find(".rental-details-fallback-box").append(view.$el);
      var close = function () { $wrap.remove(); jQuery(document).off("keydown.rfaDetails"); };
      $wrap.on("click", ".rental-details-fallback-backdrop, .rental-details-fallback-close", close);
      jQuery(document).on("keydown.rfaDetails", function (ev) { if (ev.key === "Escape" || ev.keyCode === 27) close(); });
      jQuery("body").append($wrap);
    },

    // ---- period picked from the rate table ---------------------------------
    onPeriodPick: function (e) {
      e.preventDefault();
      var card = this._cardFromEvent(e);
      if (!card) return;
      var key = this.$(e.currentTarget).data("period");
      if (_.findWhere(card.availablePeriods, { key: key })) card.period = key;
      this.render();
    },

    onPeriodKey: function (e) {
      if (e.key === "Enter" || e.key === " " || e.keyCode === 13 || e.keyCode === 32) this.onPeriodPick(e);
    },

    // Backbone.View.remove is what SCA calls when the CCT is torn down
    remove: function () {
      return Backbone.View.prototype.remove.apply(this, arguments);
    },

    // ---- per-card helpers ------------------------------------------------
    _cardFromEvent: function (e) {
      var idx = parseInt(this.$(e.currentTarget).closest("[data-card-index]").data("card-index"), 10);
      return this.cards[idx];
    },

    _setQty: function (card, qty) {
      qty = parseInt(qty, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      if (qty > DEFAULTS.maxQuantity) qty = DEFAULTS.maxQuantity;
      card.quantity = qty;
    },

    onQtyMinus: function (e) {
      e.preventDefault();
      var card = this._cardFromEvent(e);
      if (!card) return;
      this._setQty(card, card.quantity - 1);
      this.render();
    },

    onQtyPlus: function (e) {
      e.preventDefault();
      var card = this._cardFromEvent(e);
      if (!card) return;
      this._setQty(card, card.quantity + 1);
      this.render();
    },

    onQtyInput: function (e) {
      var card = this._cardFromEvent(e);
      if (!card) return;
      this._setQty(card, this.$(e.currentTarget).val());
      this.render();
    },

    onPeriodChange: function (e) {
      var card = this._cardFromEvent(e);
      if (!card) return;
      var key = this.$(e.currentTarget).val();
      if (_.findWhere(card.availablePeriods, { key: key })) {
        card.period = key;
      }
      this.render();
    },

    onLoadMore: function (e) {
      e.preventDefault();
      this.visible += this.options.pageSize;
      this.render();
    },

    onRentNow: function (e) {
      e.preventDefault();
      var self = this;
      var card = this._cardFromEvent(e);
      if (!card || card.adding || !card.inStock || !this.application) return;

      var cart = this.application.getComponent("Cart");
      if (!cart) return;

      var period = _.findWhere(PERIODS, { key: card.period });
      var rate = card.rates[card.period];
      // Avoid float artifacts (1.04 * 30 = 31.200000000000003): NetSuite rejects
      // those in the custom price option.
      var total = (Math.round(rate * period.days * 100) / 100).toFixed(2);

      card.adding = true;
      this.render();

      // Same line options the PDP flow sets (see RentFromAnywhere.View.js)
      cart.addLine({
        line: {
          quantity: card.quantity,
          item: { internalid: card.internalid },
          options: [
            { cartOptionId: "custcol_custom_price",             value: { internalid: total, label: total } },
            { cartOptionId: "custcol_configurable_description", value: { internalid: period.cartLabel, label: period.cartLabel } },
            { cartOptionId: "custcol_custom_rate",              value: { internalid: period.rateField, label: period.rateField } }
          ]
        }
      })
      .done(function () {
        cart.triggerAddToCartConfirmation();
      })
      .fail(function (err) {
        var msg = "We couldn't add this tool to your cart. Please try again.";
        try {
          var json = err && err.responseJSON;
          if (json && json.errorMessage) msg = json.errorMessage;
        } catch (ex) { /* ignore */ }
        self.errorMessage = msg;
      })
      .always(function () {
        card.adding = false;
        self.render();
      });
    },

    // ---- context -----------------------------------------------------------
    getContext: function () {
      var self = this;
      var cards = _.map(this.cards.slice(0, this.visible), function (card, i) {
        var period = _.findWhere(PERIODS, { key: card.period });
        var rate = card.rates[card.period];
        return {
          index: i,
          internalid: card.internalid,
          name: card.name,
          url: card.url,
          hasUrl: !!card.url,
          image: card.image,
          hasImage: !!card.image,
          quantity: card.quantity,
          canDecrease: card.quantity > 1,
          adding: card.adding,
          inStock: card.inStock,
          isKit: card.isKit,
          componentCount: card.components ? card.components.length : 0,
          rateFormatted: Utils.formatCurrency(rate),
          totalFormatted: Utils.formatCurrency(Math.round(rate * period.days * 100) / 100),
          // One column per available period: daily rate, period total and the
          // saving vs. the shortest period's daily rate ("-x%", grows with length)
          periods: _.map(card.availablePeriods, function (p, pi) {
            var r = card.rates[p.key];
            var base = card.rates[card.availablePeriods[0].key];
            var pct = (pi > 0 && base > 0 && r < base) ? Math.round((base - r) / base * 100) : 0;
            return {
              key: p.key,
              label: p.days + " days",
              selected: p.key === card.period,
              rateFormatted: Utils.formatCurrency(r),
              totalFormatted: Utils.formatCurrency(Math.round(r * p.days * 100) / 100),
              // "$1,208.70" needs a smaller size to fit a 5-column card
              longTotal: Math.round(r * p.days) >= 1000,
              discount: pct,
              hasDiscount: pct > 0
            };
          }),
          periodCount: card.availablePeriods.length
        };
      });

      var sortMode = this.userSort || (this.options && this.options.sort) || DEFAULTS.sort;
      var sortAsc = sortMode === "price-asc";
      return {
        heading: this.options ? this.options.heading : "",
        hasHeading: !!(this.options && this.options.heading),
        sortLabel: sortAsc ? "Price: Low to High" : "Price: High to Low",
        sortAsc: sortAsc,
        cards: cards,
        hasCards: cards.length > 0,
        isLoading: this.state === "loading",
        isEmpty: this.state === "empty",
        isError: this.state === "error",
        isLocked: this.state === "locked",
        gateFormHtml: this.state === "locked" ? AccessGate.formHtml() : "",
        errorMessage: this.errorMessage,
        hasMore: this.cards.length > this.visible,
        remaining: Math.max(this.cards.length - this.visible, 0)
      };
    }
  });
});
